import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, Compass } from 'lucide-react';

// --- Loading State (Kept simple) ---
export const LoadingState: React.FC = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      className="w-16 h-16 rounded-full border-4 border-slate-200 border-t-indigo-600 mb-6"
    />
    <motion.div
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className="flex items-center gap-2 text-slate-600 font-medium"
    >
      <MapPin size={18} className="text-indigo-600" />
      Curating area insights...
    </motion.div>
  </div>
);

// --- Error State (Improved Design and CTA structure) ---

interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
}

// CTA target link requirement: /discover-areas
const DISCOVER_AREAS_LINK = '/discover-areas';

export const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-gray-50 dark:bg-gray-900">
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="max-w-sm w-full bg-white dark:bg-gray-800 p-8 sm:p-10 rounded-[2.5rem] shadow-2xl border border-gray-200 dark:border-gray-700 text-center"
    >
      {/* Icon */}
      <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
        <MapPin className="text-red-600 dark:text-red-400" size={32} />
      </div>

      {/* Header */}
      <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">Area Not Found</h2>

      {/* Message */}
      <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed text-sm">{message}</p>

      {/* Action Buttons */}
      <div className="space-y-4">
        {/* Primary CTA (Required link) */}
        <a
          href={DISCOVER_AREAS_LINK}
          className="group w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-2xl font-bold transition-all duration-300 hover:bg-indigo-700 shadow-md hover:shadow-lg"
        >
          <Compass size={20} className="transition-transform group-hover:scale-110" />
          Explore Other Neighbourhoods
        </a>

        {/* Secondary CTA (Retry/Search Again) */}
        {onRetry && (
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 bg-transparent text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-600 py-3 rounded-2xl font-semibold hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Search size={18} />
            Search This Area Again
          </button>
        )}
      </div>
    </motion.div>
  </div>
);
